/* ═══════════════════════════════════════════════════════════════
   PiARIS — Product Story Rail  (right-rail "See it work")
   Scroll-synced, outcome-first mini-scenes: as each product card
   scrolls to the middle of the viewport, the rail swaps to that
   product's narrated scene — same spirit as the free-scan
   "screen reader's view" illustration.

   Accessibility: the animation is decorative (aria-hidden); the
   caption strip is the AT-visible narration; everything stops under
   prefers-reduced-motion. © 2026 Pi Consultants · pi-consulting.us
   ═══════════════════════════════════════════════════════════════ */
import React from "react";
import { PI_LOGO } from "../constants";

var E = React.createElement;

/* ── shared shell + per-scene keyframes ── */
var CSS = [
  ".pir-card{position:relative;border-radius:16px;padding:16px 16px 15px;min-height:calc(100vh - 96px);display:flex;flex-direction:column;border:1px solid #6366F133;overflow:hidden;animation:pir-glow 9s ease-in-out infinite}",
  "@keyframes pir-glow{0%,80%{box-shadow:0 2px 14px rgba(67,56,202,.10)}90%{box-shadow:0 6px 26px rgba(16,185,129,.30)}100%{box-shadow:0 2px 14px rgba(67,56,202,.10)}}",
  ".pir-eyebrow{font-size:9px;font-weight:800;letter-spacing:.14em;color:#6D28D9}",
  ".pir-title{font-size:15px;font-weight:900;line-height:1.2;margin:4px 0 12px;color:#0F172A;letter-spacing:-.2px}",
  ".pir-caption{margin-top:auto;font-size:10.5px;line-height:1.5;color:#334155}",
  ".pir-caption b{color:#4338CA}",
  ".pir-outcome{margin-top:10px;display:flex;align-items:center;gap:6px;font-size:10px;font-weight:800}",
  ".pir-dot{width:6px;height:6px;border-radius:50%}",
  /* ── per-scene "fun" micro-stories that fill the empty height ── */
  ".pir-fun{flex:1;display:flex;align-items:center;justify-content:center;min-height:150px;margin:6px 0 2px;overflow:hidden}",
  ".fun-stage{position:relative;width:196px;height:196px}",
  ".fun-e{position:absolute;line-height:1;will-change:transform,opacity}",
  ".fun-pi{position:absolute;object-fit:contain;background:rgba(255,255,255,.96);border-radius:50%;padding:4px;box-shadow:0 2px 10px rgba(67,56,202,.4)}",
  ".plf-frown{left:39%;top:38%;font-size:48px;animation:plf-frown 8s ease-in-out infinite}",
  "@keyframes plf-frown{0%{opacity:0;transform:scale(.6)}6%{opacity:1;transform:scale(1)}24%{opacity:1;transform:scale(.96) rotate(-3deg)}32%{opacity:0;transform:scale(.8)}100%{opacity:0}}",
  ".plf-bug{font-size:38px;animation:plf-bug 8s ease-in-out infinite}",
  "@keyframes plf-bug{0%{opacity:1;transform:rotate(0)}5%{transform:rotate(-16deg)}10%{transform:rotate(16deg)}26%{opacity:1;transform:rotate(0) scale(1)}31%{opacity:0;transform:scale(.2) rotate(30deg)}100%{opacity:0}}",
  ".plf-b1{left:13%;top:20%}",
  ".plf-b2{left:63%;top:16%;animation-delay:.5s}",
  ".plf-b3{left:65%;top:56%;animation-delay:1s}",
  ".plf-b4{left:13%;top:56%;animation-delay:1.5s}",
  ".plf-tool{font-size:36px;opacity:0;animation:plf-tool 8s ease-in-out infinite}",
  "@keyframes plf-tool{0%,10%{opacity:0;transform:translateY(-18px) scale(.4) rotate(-22deg)}20%{opacity:1;transform:translateY(0) scale(1.1) rotate(0)}26%{transform:scale(.95) rotate(16deg)}34%{opacity:0;transform:translateY(-6px) scale(.6)}100%{opacity:0}}",
  ".plf-t1{left:22%;top:12%}",
  ".plf-t2{left:71%;top:8%;animation-delay:.5s}",
  ".plf-t3{left:73%;top:48%;animation-delay:1s}",
  ".plf-t4{left:21%;top:48%;animation-delay:1.5s}",
  ".plf-ok{left:38%;top:34%;font-size:56px;opacity:0;animation:plf-ok 8s ease-in-out infinite}",
  "@keyframes plf-ok{0%,54%{opacity:0;transform:scale(.3)}62%{opacity:1;transform:scale(1.25)}68%{transform:scale(1)}82%{opacity:1}90%{opacity:0;transform:scale(.9)}100%{opacity:0}}",
  ".plf-happy{left:39%;top:54%;font-size:46px;opacity:0;animation:plf-happy 8s ease-in-out infinite}",
  "@keyframes plf-happy{0%,64%{opacity:0;transform:translateY(10px) scale(.6)}74%{opacity:1;transform:translateY(0) scale(1.1)}82%{transform:scale(1)}96%{opacity:1}100%{opacity:0}}",
  ".plf-y1{left:16%;top:40%;font-size:30px;opacity:0;animation:plf-rise 8s ease-in-out infinite .1s}",
  ".plf-y2{left:70%;top:38%;font-size:32px;opacity:0;animation:plf-rise 8s ease-in-out infinite .25s}",
  "@keyframes plf-rise{0%,66%{opacity:0;transform:translateY(10px) scale(.5)}78%{opacity:1;transform:translateY(-6px) scale(1)}92%{opacity:1;transform:translateY(-20px)}100%{opacity:0;transform:translateY(-30px)}}",
  ".fun-stage-wt{width:196px;height:196px}",
  ".wtf-worry{left:38%;top:40%;font-size:50px;animation:wtf-worry 8s ease-in-out infinite}",
  "@keyframes wtf-worry{0%{opacity:0;transform:scale(.6)}4%{opacity:1;transform:scale(1)}14%{opacity:1;transform:scale(.96) rotate(-3deg)}20%{opacity:0;transform:scale(.8)}100%{opacity:0}}",
  ".wtf-law{left:-72%;top:5%;font-size:190px;animation:wtf-law 8s ease-in-out infinite}",
  "@keyframes wtf-law{0%,8%{left:-72%;opacity:0}16%{opacity:1}30%{left:-12%;opacity:1}38%{left:-44%;opacity:0;transform:scale(.6) rotate(-12deg)}100%{opacity:0;left:-72%}}",
  ".wtf-fine{right:-72%;top:9%;font-size:190px;animation:wtf-fine 8s ease-in-out infinite}",
  "@keyframes wtf-fine{0%,38%{right:-72%;opacity:0}46%{opacity:1}58%{right:-12%;opacity:1}66%{right:-44%;opacity:0;transform:scale(.6) rotate(12deg)}100%{opacity:0;right:-72%}}",
  ".wtf-shield{left:50%;top:24%;margin-left:-48px;font-size:96px;opacity:0;animation:wtf-shield 8s ease-in-out infinite}",
  "@keyframes wtf-shield{0%,56%{opacity:0;transform:scale(.3)}66%{opacity:1;transform:scale(1.25)}72%{transform:scale(1)}84%{opacity:1}100%{opacity:0}}",
  ".wtf-pi{position:absolute;left:50%;top:54%;width:36px;height:36px;transform:translate(-50%,-50%);object-fit:contain;background:rgba(255,255,255,.94);border-radius:50%;padding:3px;box-shadow:0 1px 4px rgba(0,0,0,.28)}",
  ".wtf-sun{left:34%;top:42%;font-size:80px;opacity:0;animation:wtf-sun 8s ease-in-out infinite}",
  "@keyframes wtf-sun{0%,70%{opacity:0;transform:translateY(56px) scale(.55)}80%{opacity:1;transform:translateY(10px) scale(1.06)}90%{transform:translateY(-2px) scale(1)}97%{opacity:1;transform:translateY(-6px)}100%{opacity:0;transform:translateY(-8px)}}",
  ".dmf-worry{left:12%;top:40%;font-size:42px;animation:dmf-worry 8s ease-in-out infinite}",
  "@keyframes dmf-worry{0%{opacity:0;transform:scale(.6)}6%{opacity:1;transform:scale(1)}42%{opacity:1}50%{opacity:0;transform:translateX(-10px)}100%{opacity:0}}",
  ".dmf-doc{left:42%;top:30%;font-size:62px;animation:dmf-doc 8s ease-in-out infinite}",
  "@keyframes dmf-doc{0%{opacity:0;transform:scale(.7)}8%{opacity:1;transform:scale(1)}46%{opacity:1;transform:scale(1)}52%{opacity:0;transform:scale(.8)}100%{opacity:0}}",
  ".dmf-mute{left:55%;top:23%;font-size:30px;animation:dmf-mute 8s ease-in-out infinite}",
  "@keyframes dmf-mute{0%,8%{opacity:0;transform:scale(.5)}16%{opacity:1;transform:scale(1)}42%{opacity:1}50%{opacity:0;transform:scale(.4) rotate(20deg)}100%{opacity:0}}",
  ".dmf-pi{left:50%;top:33%;width:42px;height:42px;margin-left:-21px;opacity:0;animation:dmf-pi 8s ease-in-out infinite}",
  "@keyframes dmf-pi{0%,30%{opacity:0;transform:translateY(-22px) scale(.4)}44%{opacity:1;transform:translateY(0) scale(1.1)}52%{transform:scale(1)}60%{opacity:0;transform:scale(.7)}100%{opacity:0}}",
  ".dmf-spark{left:46%;top:25%;font-size:28px;opacity:0;animation:dmf-spark 8s ease-in-out infinite}",
  "@keyframes dmf-spark{0%,46%{opacity:0;transform:scale(.3) rotate(0)}54%{opacity:1;transform:scale(1.1) rotate(20deg)}62%{opacity:0;transform:scale(.6) rotate(40deg)}100%{opacity:0}}",
  ".dmf-open{left:41%;top:30%;font-size:64px;opacity:0;animation:dmf-open 8s ease-in-out infinite}",
  "@keyframes dmf-open{0%,52%{opacity:0;transform:scale(.6)}60%{opacity:1;transform:scale(1.12)}66%{transform:scale(1)}96%{opacity:1}100%{opacity:0}}",
  ".dmf-sound{left:29%;top:37%;font-size:30px;opacity:0;animation:dmf-sound 8s ease-in-out infinite}",
  "@keyframes dmf-sound{0%,58%{opacity:0;transform:scale(.5)}66%{opacity:1;transform:scale(1)}74%{transform:scale(1.12)}82%{transform:scale(1)}96%{opacity:1}100%{opacity:0}}",
  ".dmf-happy{left:63%;top:54%;font-size:42px;opacity:0;animation:dmf-happy 8s ease-in-out infinite}",
  "@keyframes dmf-happy{0%,60%{opacity:0;transform:translateY(10px) scale(.6)}72%{opacity:1;transform:translateY(0) scale(1.1)}80%{transform:scale(1)}96%{opacity:1}100%{opacity:0}}",
  ".dmf-heart{left:60%;top:30%;font-size:26px;opacity:0;animation:dmf-heart 8s ease-in-out infinite .3s}",
  "@keyframes dmf-heart{0%,66%{opacity:0;transform:translateY(8px) scale(.6)}78%{opacity:1;transform:translateY(-6px) scale(1)}100%{opacity:0;transform:translateY(-20px)}}",
  ".drf-worry{left:12%;top:16%;font-size:38px;animation:drf-worry 8s ease-in-out infinite}",
  "@keyframes drf-worry{0%{opacity:0;transform:scale(.6)}8%{opacity:1;transform:scale(1)}44%{opacity:1}52%{opacity:0;transform:scale(.7)}100%{opacity:0}}",
  ".drf-x{font-size:34px;animation:drf-x 8s ease-in-out infinite}",
  "@keyframes drf-x{0%,8%{opacity:0;transform:scale(.4)}16%{opacity:1;transform:scale(1)}46%{opacity:1;transform:scale(1)}55%{opacity:0;transform:translateY(22px) scale(.3) rotate(40deg)}100%{opacity:0}}",
  ".drf-x1{left:40%;top:13%}",
  ".drf-x2{left:60%;top:30%;animation-delay:.12s}",
  ".drf-x3{left:38%;top:47%;animation-delay:.24s}",
  ".drf-x4{left:62%;top:58%;animation-delay:.36s}",
  ".drf-pi{left:50%;top:30%;width:46px;height:46px;margin-left:-23px;opacity:0;animation:drf-pi 8s ease-in-out infinite}",
  "@keyframes drf-pi{0%,30%{opacity:0;transform:scale(.4) rotate(-10deg)}44%{opacity:1;transform:scale(1.1) rotate(0)}52%{transform:scale(1)}60%{opacity:0;transform:scale(.7)}100%{opacity:0}}",
  ".drf-v{font-size:36px;opacity:0;animation:drf-v 8s ease-in-out infinite}",
  "@keyframes drf-v{0%,54%{opacity:0;transform:scale(.3)}63%{opacity:1;transform:scale(1.2)}70%{transform:scale(1)}80%{opacity:1}88%{opacity:0}100%{opacity:0}}",
  ".drf-v1{left:40%;top:15%}",
  ".drf-v2{left:60%;top:32%;animation-delay:.12s}",
  ".drf-trophy{left:39%;top:30%;font-size:58px;opacity:0;animation:drf-trophy 8s ease-in-out infinite}",
  "@keyframes drf-trophy{0%,70%{opacity:0;transform:translateY(14px) scale(.5)}82%{opacity:1;transform:translateY(0) scale(1.12)}88%{transform:scale(1)}100%{opacity:0}}",
  ".drf-c1{left:17%;top:24%;font-size:28px;opacity:0;animation:drf-cheer 8s ease-in-out infinite .1s}",
  ".drf-c2{left:68%;top:22%;font-size:28px;opacity:0;animation:drf-cheer 8s ease-in-out infinite .25s}",
  "@keyframes drf-cheer{0%,72%{opacity:0;transform:translateY(10px)}84%{opacity:1;transform:translateY(-8px)}100%{opacity:0;transform:translateY(-24px)}}",
  "@media (prefers-reduced-motion:reduce){.fun-e,.fun-pi{animation:none!important;opacity:0!important;transform:none!important}.plf-ok,.plf-happy,.wtf-shield,.wtf-sun,.dmf-open,.dmf-happy,.drf-trophy,.drf-v1,.drf-v2{opacity:1!important}}",
  ".pir-bubble{position:relative;margin:0 auto;min-height:26px;width:96px;background:#fff;border:1px solid #C7D2FE;border-radius:8px;padding:5px 7px;box-shadow:0 2px 8px rgba(67,56,202,.10)}",
  ".pir-cap{position:absolute;left:7px;right:7px;top:5px;font-size:8.5px;font-weight:700;line-height:1.25}",
  /* ── progress bar (shared) ── */
  ".pir-bar{height:7px;border-radius:5px;background:#E2E8F0;overflow:hidden;margin:10px 0 4px}",
  ".pir-bar>span{display:block;height:100%;border-radius:5px}",
  ".pir-barlbl{display:flex;justify-content:space-between;font-size:8.5px;font-weight:800;color:#475569}",
  /* ── WCAG ticks ── */
  ".pir-ticks{display:flex;gap:5px;flex-wrap:wrap;margin-top:9px}",
  ".pir-tick{font-size:8px;font-weight:800;padding:2px 6px;border-radius:5px;background:#fff;border:1px solid #CBD5E1;color:#94A3B8}",
  /* ── sparkles ── */
  ".pir-spark{position:absolute;font-size:11px;opacity:0}",

  /* ════ DocMaya ════ */
  ".dm-card{background:linear-gradient(160deg,#F8FAFF,#EEF2FF 55%,#ECFDF5)}",
  ".dm-stage{position:relative;height:158px}",
  ".dm-doc{position:absolute;left:0;top:2px;width:112px;height:148px;background:#fff;border:1px solid #CBD5E1;border-radius:8px;box-shadow:0 4px 14px rgba(15,23,42,.10);overflow:hidden;animation:dm-warm 9s ease-in-out infinite}",
  "@keyframes dm-warm{0%,20%{filter:grayscale(1);opacity:.6}48%,100%{filter:grayscale(0);opacity:1}}",
  ".dm-row{height:7px;border-radius:3px;background:#E2E8F0;margin:7px 10px}",
  ".dm-row-t{height:11px;width:62%;background:linear-gradient(90deg,#4338CA,#6366F1);margin-top:14px}",
  ".dm-row-s{width:54%}",
  ".dm-img{position:relative;height:36px;margin:9px 10px;border-radius:5px;background:linear-gradient(135deg,#DBEAFE,#BFDBFE);display:flex;align-items:center;justify-content:center}",
  ".dm-img svg{opacity:.5}",
  ".dm-tag{position:absolute;font-size:8px;font-weight:800;color:#fff;padding:1px 5px;border-radius:4px;opacity:0}",
  ".dm-tag-h{top:11px;right:8px;background:#4338CA;animation:dm-tag 9s ease-in-out infinite}",
  ".dm-tag-a{top:-7px;right:-4px;background:#10B981;animation:dm-tag 9s ease-in-out infinite .22s}",
  ".dm-tag-o{bottom:8px;left:8px;background:#6D28D9;animation:dm-tag 9s ease-in-out infinite .44s}",
  "@keyframes dm-tag{0%,24%{opacity:0;transform:scale(.6)}34%{opacity:1;transform:scale(1.15)}40%,100%{opacity:1;transform:scale(1)}}",
  ".dm-sweep{position:absolute;left:0;top:0;width:100%;height:20px;background:linear-gradient(180deg,rgba(99,102,241,.4),rgba(99,102,241,0));animation:dm-sweep 9s ease-in-out infinite}",
  "@keyframes dm-sweep{0%,18%{opacity:0;transform:translateY(0)}24%{opacity:1}50%{opacity:.9;transform:translateY(120px)}56%,100%{opacity:0;transform:translateY(120px)}}",
  ".dm-listener{position:absolute;right:2px;bottom:6px;width:96px;text-align:center}",
  ".dm-face{width:56px;height:56px;animation:dm-nod 9s ease-in-out infinite}",
  "@keyframes dm-nod{0%,52%{transform:translateY(0) rotate(0)}60%{transform:translateY(2px) rotate(4deg)}70%{transform:translateY(0) rotate(0)}82%{transform:translateY(0) rotate(-3deg)}100%{transform:translateY(0)}}",
  ".dm-wave{position:absolute;top:14px;border:2px solid #10B981;border-radius:50%;opacity:0}",
  ".dm-w1{right:8px;width:14px;height:14px;animation:dm-wave 9s ease-in-out infinite}",
  ".dm-w2{right:0;width:26px;height:26px;top:8px;animation:dm-wave 9s ease-in-out infinite .18s}",
  "@keyframes dm-wave{0%,52%{opacity:0;transform:scale(.4)}60%{opacity:.9}100%{opacity:0;transform:scale(1.5)}}",
  ".dm-c0{color:#94A3B8;font-style:italic;animation:dm-c0 9s ease-in-out infinite}",
  ".dm-c1{color:#1D4ED8;animation:dm-c1 9s ease-in-out infinite}",
  ".dm-c2{color:#047857;animation:dm-c2 9s ease-in-out infinite}",
  "@keyframes dm-c0{0%,4%{opacity:0}7%,20%{opacity:1}26%,100%{opacity:0}}",
  "@keyframes dm-c1{0%,54%{opacity:0}60%,72%{opacity:1}78%,100%{opacity:0}}",
  "@keyframes dm-c2{0%,80%{opacity:0}86%,98%{opacity:1}100%{opacity:0}}",
  ".dm-fill{background:linear-gradient(90deg,#6366F1,#10B981);width:8%;animation:dm-fill 9s ease-in-out infinite}",
  "@keyframes dm-fill{0%,20%{width:6%}55%{width:62%}85%,100%{width:100%}}",
  ".dm-pct::after{content:'0%';animation:dm-pct 9s ease-in-out infinite}",
  "@keyframes dm-pct{0%,20%{content:'0%'}55%{content:'60%'}85%,100%{content:'100%'}}",
  ".dm-t1{animation:dm-ticka 9s ease-in-out infinite}",
  ".dm-t2{animation:dm-ticka 9s ease-in-out infinite .15s}",
  ".dm-t3{animation:dm-ticka 9s ease-in-out infinite .3s}",
  "@keyframes dm-ticka{0%,40%{background:#fff;border-color:#CBD5E1;color:#94A3B8}55%,100%{background:#D1FAE5;border-color:#10B98155;color:#047857}}",
  ".dm-spark{animation:dm-spark 9s ease-in-out infinite}",
  ".dm-sp1{right:30px;top:30px;animation-delay:0s}",
  ".dm-sp2{right:14px;top:64px;animation-delay:.2s}",
  ".dm-sp3{right:44px;top:74px;animation-delay:.35s}",
  "@keyframes dm-spark{0%,60%{opacity:0;transform:scale(.4) rotate(0)}68%{opacity:1;transform:scale(1.1) rotate(20deg)}82%,100%{opacity:0;transform:scale(.6) rotate(40deg)}}",

  /* ════ generic mini-scenes (PA11Y / Watchtower / Delta) ════ */
  ".ms-card{background:linear-gradient(160deg,#FFF7FB,#F5F3FF 60%,#EEF2FF)}",
  ".ms-stage{position:relative;height:150px;border-radius:10px;background:#fff;border:1px solid #E2E8F0;overflow:hidden;box-shadow:0 4px 14px rgba(15,23,42,.08)}",
  ".ms-ring{position:absolute;right:10px;top:10px;width:52px;height:52px;transform:rotate(-90deg)}",
  ".ms-ring circle{fill:none;stroke-width:6;stroke-linecap:round}",
  ".ms-ring .bg{stroke:#E2E8F0}",
  ".ms-pin{position:absolute;width:11px;height:11px;border-radius:50%;border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.2)}",
  /* PA11Y: pins flip red->green */
  ".pl-pin{background:#EF4444;animation:pl-flip 8s ease-in-out infinite}",
  "@keyframes pl-flip{0%,45%{background:#EF4444;transform:scale(1)}55%{transform:scale(1.4)}60%,100%{background:#10B981;transform:scale(1)}}",
  ".pl-ring .fg{stroke:#10B981;stroke-dasharray:138;stroke-dashoffset:138;animation:pl-ring 8s ease-in-out infinite}",
  "@keyframes pl-ring{0%,20%{stroke-dashoffset:138}60%,100%{stroke-dashoffset:11}}",
  ".pl-num::after{content:'0';animation:pl-num 8s ease-in-out infinite}",
  "@keyframes pl-num{0%,20%{content:'0'}40%{content:'48'}70%,100%{content:'92'}}",
  /* Watchtower: radar sweep + dots lighting */
  ".pir-wt-radar{position:absolute;left:50%;top:54%;width:0;height:84px;transform-origin:top;background:linear-gradient(180deg,rgba(67,56,202,.5),transparent);box-shadow:0 0 0 1px rgba(67,56,202,.4);animation:wt-spin 4s linear infinite}",
  "@keyframes wt-spin{0%{transform:translateX(-50%) rotate(0)}100%{transform:translateX(-50%) rotate(360deg)}}",
  ".wt-dot{position:absolute;width:8px;height:8px;border-radius:50%;background:#CBD5E1;animation:wt-ping 4s ease-in-out infinite}",
  "@keyframes wt-ping{0%,100%{background:#CBD5E1}50%{background:#6366F1;box-shadow:0 0 8px #6366F1}}",
  ".wt-ring .fg{stroke:#6366F1;stroke-dasharray:138;stroke-dashoffset:138;animation:pl-ring 8s ease-in-out infinite}",
  /* Delta: before/after bars */
  ".dr-bars{position:absolute;inset:0;display:flex;align-items:flex-end;justify-content:center;gap:18px;padding:16px}",
  ".dr-bar{width:34px;border-radius:6px 6px 0 0;display:flex;align-items:flex-start;justify-content:center;color:#fff;font-size:9px;font-weight:800;padding-top:3px}",
  ".dr-before{height:78%;background:linear-gradient(180deg,#F87171,#EF4444)}",
  ".dr-after{height:18%;background:linear-gradient(180deg,#34D399,#10B981);animation:dr-grow 7s ease-in-out infinite}",
  "@keyframes dr-grow{0%,30%{height:78%;background:linear-gradient(180deg,#F87171,#EF4444)}55%,100%{height:18%;background:linear-gradient(180deg,#34D399,#10B981)}}",
  ".dr-lbl{position:absolute;bottom:4px;width:100%;text-align:center;font-size:8px;font-weight:700;color:#64748B}",

  /* ════ "Screen reader's view" panel — reuses the free-scan page's
     travelling focus-ring animation; sits ABOVE the story scene ════ */
  ".sr-scene{position:relative;border-radius:16px;padding:16px 16px 16px;min-height:calc(100vh - 96px);display:flex;flex-direction:column;border:1px solid #6366F133;background:linear-gradient(160deg,#F8FAFF,#EEF2FF 55%,#ECFDF5);box-shadow:0 2px 14px rgba(67,56,202,.10)}",
  ".sr-mid{flex:1;display:flex;flex-direction:column;justify-content:center;min-height:0}",
  ".sr-stats{display:grid;grid-template-columns:1fr 1fr;gap:7px;margin-top:14px}",
  ".sr-stat{background:rgba(255,255,255,.72);border:1px solid #C7D2FE66;border-radius:9px;padding:8px 9px}",
  ".sr-statn{font-size:16px;font-weight:900;color:#4338CA;line-height:1}",
  ".sr-statl{font-size:8px;color:#475569;margin-top:3px;line-height:1.3;font-weight:600}",
  ".sr-cta-btn{display:block;text-align:center;margin:2px 0 12px;padding:10px 14px;border-radius:10px;background:linear-gradient(135deg,#4338CA,#6366F1);color:#fff;font-size:12px;font-weight:800;text-decoration:none;box-shadow:0 6px 18px rgba(67,56,202,.28)}",
  ".sr-cta-btn:hover{filter:brightness(1.06)}",
  ".br-stage{position:relative;margin:2px 0 0;height:134px}",
  ".br-win{position:relative;width:184px;height:132px;margin:0 auto;background:linear-gradient(180deg,#F8FAFC,#FFFFFF);border:1px solid #E2E8F0;border-radius:9px;box-shadow:0 5px 16px rgba(15,23,42,.12);overflow:hidden}",
  ".br-tabs{position:absolute;top:7px;left:8px;display:flex;gap:4px;z-index:1}",
  ".br-dot{width:6px;height:6px;border-radius:50%;background:#CBD5E1}",
  ".br-el{position:absolute;border-radius:4px;background:#E2E8F0}",
  ".br-logo{top:24px;left:8px;width:38px;height:13px;background:#CBD5E1}",
  ".br-nav{top:24px;left:52px;width:124px;height:13px}",
  ".br-h1{top:45px;left:8px;width:168px;height:20px;background:#C7D2FE}",
  ".br-btn{top:73px;left:8px;width:54px;height:17px;background:#6366F1}",
  ".br-p{top:97px;left:8px;width:168px;height:24px;background:#F1F5F9}",
  ".pf-ring{position:absolute;border:2.5px solid #4F46E5;border-radius:6px;box-shadow:0 0 0 3px rgba(79,70,229,.18);box-sizing:border-box;z-index:2;top:24px;left:8px;width:38px;height:13px;animation:pf-travel 9s ease-in-out infinite}",
  "@keyframes pf-travel{0%,15%{top:24px;left:8px;width:38px;height:13px}20%,33%{top:24px;left:52px;width:124px;height:13px}38%,53%{top:45px;left:8px;width:168px;height:20px}58%,71%{top:73px;left:8px;width:54px;height:17px}76%,93%{top:97px;left:8px;width:168px;height:24px}100%{top:24px;left:8px;width:38px;height:13px}}",
  ".pf-strip{position:relative;min-height:34px;margin-top:9px;padding:6px 9px 7px;background:#0F172A;border-radius:8px;overflow:hidden}",
  ".pf-lbl{font-size:7.5px;font-weight:800;letter-spacing:.1em;text-transform:uppercase;color:#94A3B8;display:flex;align-items:center;gap:4px}",
  ".pf-on{width:5px;height:5px;border-radius:50%;background:#34D399;flex-shrink:0;animation:pf-on 1.4s ease-in-out infinite}",
  "@keyframes pf-on{0%,100%{opacity:1}50%{opacity:.35}}",
  ".pf-cap{position:absolute;left:9px;right:9px;top:20px;font-size:9px;font-weight:600;font-family:Consolas,monospace;color:#A5B4FC;opacity:0;line-height:1.3;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}",
  ".pf-cur{display:inline-block;width:5px;height:9px;margin-left:2px;vertical-align:-1px;background:#34D399;animation:pf-blink 1s steps(1) infinite}",
  "@keyframes pf-blink{0%,50%{opacity:1}51%,100%{opacity:0}}",
  ".pf-c1{animation:pf-k1 9s ease-in-out infinite}",
  ".pf-c2{animation:pf-k2 9s ease-in-out infinite}",
  ".pf-c3{animation:pf-k3 9s ease-in-out infinite}",
  ".pf-c4{animation:pf-k4 9s ease-in-out infinite}",
  ".pf-c5{animation:pf-k5 9s ease-in-out infinite}",
  "@keyframes pf-k1{0%{opacity:0}3%,15%{opacity:1}19%,100%{opacity:0}}",
  "@keyframes pf-k2{0%,17%{opacity:0}21%,33%{opacity:1}37%,100%{opacity:0}}",
  "@keyframes pf-k3{0%,35%{opacity:0}40%,53%{opacity:1}57%,100%{opacity:0}}",
  "@keyframes pf-k4{0%,55%{opacity:0}59%,71%{opacity:1}75%,100%{opacity:0}}",
  "@keyframes pf-k5{0%,73%{opacity:0}77%,93%{opacity:1}97%,100%{opacity:0}}",
  "@media (prefers-reduced-motion:reduce){.pf-ring,.pf-on,.pf-cur,.pf-c1,.pf-c2,.pf-c3,.pf-c4,.pf-c5{animation:none!important}.pf-ring{top:45px;left:8px;width:168px;height:20px}.pf-c3{opacity:1}}",

  "@media (prefers-reduced-motion:reduce){.pir-card,.dm-doc,.dm-sweep,.dm-tag-h,.dm-tag-a,.dm-tag-o,.dm-w1,.dm-w2,.dm-face,.dm-c0,.dm-c1,.dm-c2,.dm-fill,.dm-spark,.pl-pin,.pl-ring .fg,.pir-wt-radar,.wt-dot,.wt-ring .fg,.dr-after{animation:none!important}.dm-doc{filter:none;opacity:1}.dm-c1{opacity:1}.dm-c0,.dm-c2,.dm-sweep,.dm-w1,.dm-w2,.dm-spark{opacity:0}.dm-fill{width:100%}.pl-pin{background:#10B981}.pl-ring .fg{stroke-dashoffset:11}.dr-after{height:18%;background:#10B981}}"
].join("\n");

function listener() {
  return E("div", { className: "dm-listener" },
    E("span", { className: "dm-wave dm-w1" }),
    E("span", { className: "dm-wave dm-w2" }),
    E("svg", { className: "dm-face", viewBox: "0 0 64 64" },
      E("circle", { cx: 32, cy: 36, r: 15, fill: "#FDE68A", stroke: "#B45309", strokeWidth: 1.5 }),
      E("path", { d: "M16 33 a16 16 0 0 1 32 0", fill: "none", stroke: "#4338CA", strokeWidth: 3 }),
      E("rect", { x: 13, y: 31, width: 6, height: 12, rx: 3, fill: "#4338CA" }),
      E("rect", { x: 45, y: 31, width: 6, height: 12, rx: 3, fill: "#4338CA" }),
      E("circle", { cx: 27, cy: 35, r: 1.6, fill: "#1E293B" }),
      E("circle", { cx: 37, cy: 35, r: 1.6, fill: "#1E293B" }),
      E("path", { d: "M27 41 q5 4 10 0", fill: "none", stroke: "#1E293B", strokeWidth: 2, strokeLinecap: "round" })),
    E("div", { className: "pir-bubble", style: { marginTop: 3 } },
      E("span", { className: "pir-cap dm-c0" }, "“…silence. Untagged.”"),
      E("span", { className: "pir-cap dm-c1" }, "“Heading: Annual Report”"),
      E("span", { className: "pir-cap dm-c2" }, "“Figure: revenue, up 12%”")));
}

/* Per-scene "fun" micro-stories — relatable to each product's value,
   framed positively (a problem → the PARIS/Pi hero → a joyful payoff).
   All decorative (aria-hidden); they freeze under prefers-reduced-motion. */

/* PA11Y: a frustrated user + bugs; each bug gets its own remediation tool
   (🔧 fix, 🔨 build, 🧴 spray, 🩹 patch) → all clear → happy, free. */
function funPally() {
  return E("div", { className: "pir-fun", "aria-hidden": "true" },
    E("div", { className: "fun-stage" },
      E("span", { className: "fun-e plf-frown" }, "😣"),
      E("span", { className: "fun-e plf-bug plf-b1" }, "🐛"),
      E("span", { className: "fun-e plf-bug plf-b2" }, "🐛"),
      E("span", { className: "fun-e plf-bug plf-b3" }, "🐛"),
      E("span", { className: "fun-e plf-bug plf-b4" }, "🐛"),
      E("span", { className: "fun-e plf-tool plf-t1" }, "🔧"),
      E("span", { className: "fun-e plf-tool plf-t2" }, "🔨"),
      E("span", { className: "fun-e plf-tool plf-t3" }, "🧴"),
      E("span", { className: "fun-e plf-tool plf-t4" }, "🩹"),
      E("span", { className: "fun-e plf-ok" }, "✅"),
      E("span", { className: "fun-e plf-happy" }, "😄"),
      E("span", { className: "fun-e plf-y1" }, "👍"),
      E("span", { className: "fun-e plf-y2" }, "🎉")));
}
/* Watchtower: worried by a looming lawsuit & fine → the PARIS shield (Pi
   crest) blocks them → a smiling sun rises. */
function funWatch() {
  return E("div", { className: "pir-fun", "aria-hidden": "true" },
    E("div", { className: "fun-stage fun-stage-wt" },
      E("span", { className: "fun-e wtf-worry" }, "😰"),
      E("span", { className: "fun-e wtf-law" }, "⚖️"),
      E("span", { className: "fun-e wtf-fine" }, "💸"),
      E("span", { className: "fun-e wtf-shield" }, "🛡️", E("img", { className: "wtf-pi", src: PI_LOGO, alt: "" })),
      E("span", { className: "fun-e wtf-sun" }, "🌞")));
}
/* DocMaya: a silent PDF a reader can't use → the Pi agent opens it up
   (tags + reading order) → read aloud → a happy reader. */
function funDoc() {
  return E("div", { className: "pir-fun", "aria-hidden": "true" },
    E("div", { className: "fun-stage" },
      E("span", { className: "fun-e dmf-worry" }, "😟"),
      E("span", { className: "fun-e dmf-doc" }, "📄"),
      E("span", { className: "fun-e dmf-mute" }, "🔇"),
      E("img", { className: "fun-pi dmf-pi", src: PI_LOGO, alt: "" }),
      E("span", { className: "fun-e dmf-spark" }, "✨"),
      E("span", { className: "fun-e dmf-open" }, "📖"),
      E("span", { className: "fun-e dmf-sound" }, "🔊"),
      E("span", { className: "fun-e dmf-happy" }, "😄"),
      E("span", { className: "fun-e dmf-heart" }, "❤️")));
}
/* Delta Recon: many issues + worry → the Pi agent diffs two scans →
   issues resolve to ✅ → progress proven (trophy + cheers). */
function funDelta() {
  return E("div", { className: "pir-fun", "aria-hidden": "true" },
    E("div", { className: "fun-stage" },
      E("span", { className: "fun-e drf-worry" }, "😟"),
      E("span", { className: "fun-e drf-x drf-x1" }, "❌"),
      E("span", { className: "fun-e drf-x drf-x2" }, "❌"),
      E("span", { className: "fun-e drf-x drf-x3" }, "❌"),
      E("span", { className: "fun-e drf-x drf-x4" }, "❌"),
      E("img", { className: "fun-pi drf-pi", src: PI_LOGO, alt: "" }),
      E("span", { className: "fun-e drf-v drf-v1" }, "✅"),
      E("span", { className: "fun-e drf-v drf-v2" }, "✅"),
      E("span", { className: "fun-e drf-trophy" }, "🏆"),
      E("span", { className: "fun-e drf-c1" }, "🎉"),
      E("span", { className: "fun-e drf-c2" }, "🎉")));
}

function DocMayaScene() {
  return E("div", { className: "pir-card dm-card" },
    E("div", { className: "pir-eyebrow" }, "SEE IT WORK · DOCMAYA"),
    E("h3", { className: "pir-title" }, "Your PDF, readable by everyone."),
    E("div", { className: "dm-stage", "aria-hidden": "true" },
      E("span", { className: "pir-spark dm-spark dm-sp1" }, "✨"),
      E("span", { className: "pir-spark dm-spark dm-sp2" }, "✨"),
      E("span", { className: "pir-spark dm-spark dm-sp3" }, "✨"),
      E("div", { className: "dm-doc" },
        E("div", { className: "dm-tag dm-tag-h" }, "H1"),
        E("div", { className: "dm-row dm-row-t" }),
        E("div", { className: "dm-img" },
          E("span", { className: "dm-tag dm-tag-a" }, "alt"),
          E("svg", { width: 18, height: 18, viewBox: "0 0 24 24", fill: "none", stroke: "#3B82F6", strokeWidth: 2 },
            E("rect", { x: 3, y: 3, width: 18, height: 18, rx: 2 }),
            E("circle", { cx: 8.5, cy: 8.5, r: 1.5 }),
            E("path", { d: "M21 15l-5-5L5 21" }))),
        E("div", { className: "dm-row" }),
        E("div", { className: "dm-row dm-row-s" }),
        E("div", { className: "dm-tag dm-tag-o" }, "order"),
        E("div", { className: "dm-sweep" })),
      listener()),
    /* readability meter climbs as tags land */
    E("div", { className: "pir-barlbl" }, E("span", null, "Screen-reader access"), E("span", { className: "dm-pct" })),
    E("div", { className: "pir-bar" }, E("span", { className: "dm-fill" })),
    /* WCAG criteria tick green */
    E("div", { className: "pir-ticks" },
      E("span", { className: "pir-tick dm-t1" }, "1.1.1 alt ✓"),
      E("span", { className: "pir-tick dm-t2" }, "1.3.1 tags ✓"),
      E("span", { className: "pir-tick dm-t3" }, "1.3.2 order ✓")),
    funDoc(),
    E("div", { className: "pir-caption", role: "region", "aria-label": "What DocMaya does" },
      E("b", null, "DocMaya"), " adds the tags, alt text, and reading order to your PDF — so a screen-reader user hears it exactly the way you meant it."),
    E("div", { className: "pir-outcome", style: { color: "#047857" } },
      E("span", { className: "pir-dot", style: { background: "#10B981" } }),
      "From invisible → fully readable, in one pass."));
}

function Pa11yScene() {
  var pins = [[20, 30], [70, 26], [38, 70], [80, 80], [54, 50]];
  return E("div", { className: "pir-card ms-card" },
    E("div", { className: "pir-eyebrow" }, "SEE IT WORK · PA11Y LAB"),
    E("h3", { className: "pir-title" }, "Find every issue — then fix it."),
    E("div", { className: "ms-stage", "aria-hidden": "true" },
      E("div", { className: "dm-row dm-row-t", style: { marginTop: 14 } }),
      E("div", { className: "dm-row" }), E("div", { className: "dm-row dm-row-s" }),
      pins.map(function (xy, i) { return E("span", { key: i, className: "ms-pin pl-pin", style: { left: xy[0] + "%", top: xy[1] + "%", animationDelay: (i * 0.12) + "s" } }); }),
      E("svg", { className: "ms-ring pl-ring", viewBox: "0 0 52 52" },
        E("circle", { className: "bg", cx: 26, cy: 26, r: 22 }),
        E("circle", { className: "fg", cx: 26, cy: 26, r: 22 }))),
    E("div", { className: "pir-barlbl", style: { marginTop: 12 } }, E("span", null, "Score"), E("span", null, E("span", { className: "pl-num" }), "%")),
    funPally(),
    E("div", { className: "pir-caption", role: "region", "aria-label": "What PA11Y Lab does" },
      E("b", null, "PA11Y Lab"), " scans your pages and PDFs against WCAG 2.2 AA, pinpoints each barrier, and walks you through the fix — free."),
    E("div", { className: "pir-outcome", style: { color: "#6D28D9" } },
      E("span", { className: "pir-dot", style: { background: "#8B5CF6" } }), "5 issues found → 0. Score 0 → 92%."));
}

function WatchtowerScene() {
  var dots = [[24, 30], [62, 24], [76, 52], [40, 60], [20, 64], [58, 74]];
  return E("div", { className: "pir-card ms-card", style: { background: "linear-gradient(160deg,#F5F7FF,#EEF2FF 60%,#E0F2FE)" } },
    E("div", { className: "pir-eyebrow" }, "SEE IT WORK · WATCHTOWER"),
    E("h3", { className: "pir-title" }, "Watch the whole domain — always."),
    E("div", { className: "ms-stage", "aria-hidden": "true" },
      dots.map(function (xy, i) { return E("span", { key: i, className: "wt-dot", style: { left: xy[0] + "%", top: xy[1] + "%", animationDelay: (i * 0.5) + "s" } }); }),
      E("div", { className: "pir-wt-radar" }),
      E("svg", { className: "ms-ring wt-ring", viewBox: "0 0 52 52" },
        E("circle", { className: "bg", cx: 26, cy: 26, r: 22 }),
        E("circle", { className: "fg", cx: 26, cy: 26, r: 22 }))),
    E("div", { className: "pir-barlbl", style: { marginTop: 12 } }, E("span", null, "847 pages crawled"), E("span", null, "12.4k issues")),
    funWatch(),
    E("div", { className: "pir-caption", role: "region", "aria-label": "What Watchtower does" },
      E("b", null, "Watchtower"), " crawls every page and linked PDF, scores compliance, and keeps watching — so a regression never slips back in."),
    E("div", { className: "pir-outcome", style: { color: "#4338CA" } },
      E("span", { className: "pir-dot", style: { background: "#6366F1" } }), "Full-domain coverage, monitored 24/7."));
}

function DeltaScene() {
  return E("div", { className: "pir-card ms-card", style: { background: "linear-gradient(160deg,#FFF7ED,#FEF2F2 50%,#ECFDF5)" } },
    E("div", { className: "pir-eyebrow" }, "SEE IT WORK · DELTA RECON"),
    E("h3", { className: "pir-title" }, "Prove the progress you made."),
    E("div", { className: "ms-stage", "aria-hidden": "true" },
      E("div", { className: "dr-bars" },
        E("div", { className: "dr-bar dr-before" }, "318"),
        E("div", { className: "dr-bar dr-after" }, "42")),
      E("div", { className: "dr-lbl" }, "last scan → this scan")),
    funDelta(),
    E("div", { className: "pir-caption", role: "region", "aria-label": "What Delta Recon does", style: { marginTop: 12 } },
      E("b", null, "Delta Recon"), " diffs two scans so you can show exactly what got fixed — and catch anything that regressed."),
    E("div", { className: "pir-outcome", style: { color: "#047857" } },
      E("span", { className: "pir-dot", style: { background: "#10B981" } }), "276 resolved · 8 regressions flagged."));
}

var SCENES = { screenreader: ScreenReaderScene, docmaya: DocMayaScene, pally: Pa11yScene, watchtower: WatchtowerScene, compare: DeltaScene };
var ORDER = ["pally", "watchtower", "compare", "docmaya"];

/** ScreenReaderScene — the free-scan page's travelling-focus-ring
    "screen reader's view" animation, full-height for the rail. This is the
    DEFAULT scene shown at the top (before any product card is centered);
    the rail swaps to a product scene as you scroll to its card. */
function ScreenReaderScene() {
  var caps = [
    "“Logo — link”",
    "“Navigation · 4 links”",
    "“Heading 1 — Accessibility Matters”",
    "“Get Started — button”",
    "“Paragraph — Our services help…”"
  ];
  var stats = [
    ["1.3B", "people live with a disability worldwide"],
    ["96.3%", "of home pages fail a WCAG check"],
    ["48hr", "free-scan report turnaround"],
    ["$0", "no card, no sales pressure"]
  ];
  return E("div", { className: "sr-scene" },
    E("div", { className: "pir-eyebrow" }, "WHY THIS MATTERS"),
    E("h3", { className: "pir-title" }, "A screen reader’s view of your site."),
    E("div", { className: "pir-caption", style: { marginTop: 0, marginBottom: 10, color: "#475569" } },
      "If the focus ring can’t reach a heading, button, or link — neither can your visitor."),
    E("a", { className: "sr-cta-btn", href: "/free-scan" }, "Get a free scan ", E("span", { "aria-hidden": "true" }, "→")),
    E("div", { className: "sr-mid" },
      E("div", { className: "br-stage", "aria-hidden": "true" },
        E("div", { className: "br-win" },
          E("div", { className: "br-tabs" },
            E("span", { className: "br-dot" }), E("span", { className: "br-dot" }), E("span", { className: "br-dot" })),
          E("div", { className: "br-el br-logo" }),
          E("div", { className: "br-el br-nav" }),
          E("div", { className: "br-el br-h1" }),
          E("div", { className: "br-el br-btn" }),
          E("div", { className: "br-el br-p" }),
          E("div", { className: "pf-ring" }))),
      E("div", { className: "pf-strip", role: "region", "aria-label": "Screen reader announcement preview" },
        E("div", { className: "pf-lbl" }, E("span", { className: "pf-on" }), "Screen reader says"),
        caps.map(function (t, i) {
          return E("span", { key: i, className: "pf-cap pf-c" + (i + 1) }, t, E("span", { className: "pf-cur" }));
        }))),
    E("div", { className: "sr-stats", "aria-hidden": "true" },
      stats.map(function (s, i) {
        return E("div", { key: i, className: "sr-stat" },
          E("div", { className: "sr-statn" }, s[0]),
          E("div", { className: "sr-statl" }, s[1]));
      })));
}

/** ProductStoryRail — sticky right rail; scene scroll-syncs to the
    product card nearest the middle of the viewport. */
function ProductStoryRail() {
  var av = React.useState("screenreader");
  var active = av[0], setActive = av[1];

  React.useEffect(function () {
    var els = Array.prototype.slice.call(document.querySelectorAll("[data-story-card]"));
    if (!els.length || typeof IntersectionObserver === "undefined") return;
    var ratios = {};
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { ratios[e.target.getAttribute("data-story-card")] = e.isIntersecting ? e.intersectionRatio : 0; });
      var best = null, bestR = 0;
      ORDER.forEach(function (k) { if ((ratios[k] || 0) > bestR) { bestR = ratios[k]; best = k; } });
      /* No product card in the center band (top hero / free-scan area, or
         below the last product) → show the screen-reader scene. */
      setActive(best || "screenreader");
    }, { rootMargin: "-35% 0px -35% 0px", threshold: [0, 0.25, 0.5, 0.75, 1] });
    els.forEach(function (el) { io.observe(el); });
    return function () { io.disconnect(); };
  }, []);

  var Scene = SCENES[active] || DocMayaScene;
  return E("aside", {
    "aria-label": "See PiARIS work",
    className: "pir-rail",
    style: { position: "sticky", top: 80, width: 230, flexShrink: 0, alignSelf: "flex-start" }
  },
    E("style", null, CSS),
    E(Scene, { key: active })
  );
}

export { ProductStoryRail };
